/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable @typescript-eslint/ban-types */
// @ts-nocheck
import { useRef, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { ERROR_EVENT, BACKEND_ERROR_EVENT, bindEditorViewApi } from '../api';
import { EditorState } from 'prosemirror-state';
import { history } from 'prosemirror-history';
import { keymap } from 'prosemirror-keymap';
import { buildKeymap } from '../../editor-core';
import { baseKeymap } from 'prosemirror-commands';
import { emDash, inputRules } from 'prosemirror-inputrules';
import { TopMenuPlugin } from '../../editor-core';
import { columnResizing, fixTables, tableEditing } from 'prosemirror-tables-contently';
import { PromptMenuPlugin } from '../../editor-core';
import { FloatingMenuPlugin } from '../../editor-core';
import { TrackedPlugin } from '../../editor-core';
import { AnnotationPlugin } from '../../editor-core';
import { UIStatePlugin } from '../../editor-core';
import { uiStatePluginKey } from '../../editor-core';
import { EditorView } from 'prosemirror-view';
import useYWebsocketContext from "../../hooks/useYWebsocketContext";

const consumerApiEventMap = {
  clipboardCopyError: ERROR_EVENT,
  loadContentError: ERROR_EVENT,
  saveContentError: BACKEND_ERROR_EVENT,
};

// export interface IMenuOptions {
//   topMenu: boolean;
//   promptMenu: boolean;
//   floatingMenu: boolean;
// }
//
// export interface IProsemirrorOptions {
//   consumerApi?: ConsumerApi;
//   schema: Schema;
//   menus: IMenuOptions;
//   characterLimit?: number;
//   plugins?: any[];
// }

/**
 * Initialize the ProseMirror EditorView object with the given state
 *
 * @param {EditorState} state ProseMirror document state object
 * @param {function} dispatchTransaction Callback that receives state update transactions produced by this EditorView
 * @returns EditorView
 */
function initView(state, dispatchTransaction) {
  // Check the tables in the current Prosemirror state and fix them if they're broken
  // (e.g., no overlapping cells, rows all equal width, etc)
  const fix = fixTables(state);
  if (fix) state = state.apply(fix.setMeta("addToHistory", false));

  const editorView = new EditorView(undefined, {
    state,
    dispatchTransaction,
    editable: (editorState) => {
      return !uiStatePluginKey.getState(editorState).readOnly;
    },
    attributes: {
      role: 'input'
    }
  });

  return editorView;
}

/**
 * React hook to set up a Prosemirror editor connected to the given content.
 *
 * @param {string} roomName The roomName of the content. This is in the format [storyId]-[Prosemirror schema]-[content identifier]. The content identifier is 'body', 'headline' or the ID of a custom field.
 * @param {function} onDispatchTransaction Callback that receives state update transactions produced by this Prosemirror editor.
 * @param {Object} options Prosemirror options
 * @returns {Object} prosemirrorObjects Objects necessary for a healthy and functional Prosemirror editor
 * @returns {boolean} prosemirrorObjects.editorLoading Whether or not the editor is in the process of loading
 * @returns {boolean} prosemirrorObjects.editorReady Whether or not the editor state is loaded and ready to be used
 * @returns {React.Ref} prosemirrorObjects.editorViewRef React DOM reference to the Prosemirror EditorView object 
 * @returns {React.Ref} prosemirrorObjects.editorEl React DOM reference we can attach to the div wrapping our Prosemirror editor
 * @returns {EditorState|null} prosemirrorObjects.editorState Prosemirror Editor state (once it's finished loading)
 */
function useProsemirror(
    roomName,
    onDispatchTransaction,
    options
) {
  // @ts-ignore
  const { consumerApi, title, format } = options;
  const editorEl = useRef(null);
  const editorViewRef = useRef(null);
  const [editorReady, setEditorReady] = useState(false);
  const [editorState, setEditorState] = useState(null);
  const [editorLoading, setEditorLoading] = useState(true);
  const yWebsocket = useYWebsocketContext();

  // Load the content from the y-websocket server and create a Prosemirror EditorState from it
  useEffect(() => {
    if (!editorState) {
      const fetchData = async () => {
        try {
          axios.get(yWebsocket.url + '/serializeProsemirror/' + roomName, {
            headers: {
              Authorization: yWebsocket.jwt
            }
          }).then((response) => {
            setEditorLoading(false);

            const json = response.data;
            const cursorPos = json.content.length ? 1 : 0

            setEditorState(EditorState.fromJSON(
                {
                  schema: options.schema,
                  plugins: getEditorPlugins(
                      options.schema,
                      options.menus.topMenu,
                      options.menus.promptMenu,
                      options.menus.floatingMenu
                  )
                },
                {
                  "doc": json,
                  "selection": {
                    "type": "text",
                    "anchor": cursorPos,
                    "head":cursorPos
                  }
                }
            ));
          });
        } catch (e) {
          setEditorLoading(false);
          console.log(e);
          if (consumerApi) consumerApi.emit(consumerApiEventMap['loadContentError'], e);
          return null;
        }
      };
      fetchData();
    }
  },[]);

  const dispatchTransaction = useCallback((tx)=> {
    const oldState = editorViewRef.current.state;
    const newState = oldState.apply(tx);
    editorViewRef.current.updateState(newState);
    setEditorState(newState);
    const eventTypes = Object.keys(tx.meta);
    if (consumerApi) {
      for (const eventType of eventTypes) {
        // @ts-ignore
        if (consumerApiEventMap[eventType]) {
          // @ts-ignore
          consumerApi.emit(consumerApiEventMap[eventType], tx.meta[eventType]);
        }
      }
    }
    onDispatchTransaction(tx);
  }, [consumerApi, onDispatchTransaction]);

  // Init editor view and setup plugins when editorState is ready
  useEffect(() => {
    if (editorState && !editorViewRef.current) {
      const view = initView(editorState, dispatchTransaction);
      editorViewRef.current = view;
      if (!view.dom.innerText && format === "headline") view.dom.innerText = title;
      if (consumerApi) bindEditorViewApi(consumerApi, editorViewRef.current);
      if (editorEl.current) {
        editorEl.current.appendChild(view.dom);
      }
      setEditorReady(true);
      if (consumerApi) consumerApi.ready();
    }
  }, [editorState, dispatchTransaction, consumerApi, format, title]);

  return { editorReady, editorViewRef, editorState, editorEl, editorLoading };
}

function getEditorPlugins(
  schema,
  topMenu = true,
  promptMenu= true,
  floatingMenu= true
) {
  const plugins = [history(), keymap(buildKeymap(schema)), keymap(baseKeymap), inputRules({rules: [emDash]})];

  if (topMenu) {
    plugins.push(TopMenuPlugin(schema));
    if (schema.nodes.table)
      plugins.push(tableEditing(), columnResizing({ cellMinWidth: 20 }));
  }

  if (promptMenu) plugins.push(PromptMenuPlugin(schema));
  if (floatingMenu) plugins.push(FloatingMenuPlugin(schema));

  return [...plugins, ...[TrackedPlugin(), AnnotationPlugin(schema), UIStatePlugin()]];
}

export default useProsemirror;



// WEBPACK FOOTER //
// ./app/javascript/client/editor-ui/hooks/useProsemirror.js